import {
  createQuery,
  createSuspenseQuery
} from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";
import { ReminderSchema } from "../../schemas/reminder.js";

const ViewRemindersResponseSchema = z.array(
  z.object({
    description: z.string().nullish(),
    entity_name: z.string().min(1).nullish(),
    alert_date: z.string().nullish(),
    status: z.string(),
    reminder_date: z.string(),
    reminder_time: z.string(),
    repeat_count: z.int().nullish(),
    recurring_interval: ReminderSchema.shape.recurring_interval,
    _group_value: z.string().nullish(),
    _group_count: z.int().nullish()
  })
);

// Add discriminated union for wf and public requests
const wfRequest = z.object({
  api: z.literal("wf"),
  id: z.string()
});
const publicRequest = z.object({
  api: z.literal("public"),
  slug: z.string(),
  id: z.string()
});
const ViewRemindersRequestSchema = z.discriminatedUnion("api", [
  wfRequest,
  publicRequest
]);
const ViewRemindersRequestVariables = z.union([
  wfRequest.omit({ api: true }),
  publicRequest.omit({ api: true })
]);

export type ViewRemindersRequest = z.infer<typeof ViewRemindersRequestSchema>;
export type ViewRemindersResponse = z.infer<typeof ViewRemindersResponseSchema>;

// URL mappings for wf and public APIs
const urls = {
  wf: "/views/:id/reminders",
  public: "/public/:slug/boardviews/:id/reminders"
};

export const useViewReminders = createQuery<
  ViewRemindersResponse,
  ViewRemindersRequest,
  Error
>({
  queryKey: ["ViewReminders"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: viewRemindersAPI, ...rest } = variables;
    return api<Omit<ViewRemindersRequest, "api">, ViewRemindersResponse>({
      method: "GET",
      url: urls[viewRemindersAPI],
      requestSchema: ViewRemindersRequestVariables,
      responseSchema: ViewRemindersResponseSchema,
      body: rest
    });
  }
});

export const useViewRemindersWithSuspense = createSuspenseQuery<
  ViewRemindersResponse,
  ViewRemindersRequest,
  Error
>({
  queryKey: ["ViewRemindersWithSuspense"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: viewRemindersAPI, ...rest } = variables;
    return api<Omit<ViewRemindersRequest, "api">, ViewRemindersResponse>({
      method: "GET",
      url: urls[viewRemindersAPI],
      requestSchema: ViewRemindersRequestVariables,
      responseSchema: ViewRemindersResponseSchema,
      body: rest
    });
  }
});
